import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Loader2, Lock } from 'lucide-react';
import { Button } from 'components/ui/button';
import { successToast } from 'hooks/use-toast';
import { useTheme } from 'components/core/theme-provider';
import { cn } from 'lib/utils';
import { useCommonStore } from 'state/store/common/useCommonStore';
import Buddy from 'features/buddy/Buddy';
import { TopUpForm } from 'features/credits/components/topup-form';
import { useCreditSummary } from 'features/credits/hooks/use-credits';
import { AddSeatDialog } from 'features/iam/components/seats/add-seat-dialog';
import { useGivePlan } from 'features/iam/components/seats/use-give-plan';
import { planUnavailableNote } from 'features/iam/components/seats/plan-actions';
import { PendingCheckoutAction } from 'features/iam/components/seats/pending-checkout-action';
import { usePlanSeats } from '../hooks/use-seats';
import { isEnterprisePlanCode } from '../constants/plan-display';
import { primaryButtonClass } from '../constants/button-styles';
import { TOP_UP_TAB, uniquePlanIntervals } from '../utils/billing-cycle';
import { defaultSeatInterval, formatMinor, preferredPrice, withInterval } from '../utils/seat-pools';
import { BillingCycleToggle, discountedAmountMinor } from './subscription-plan';

const NO_PLAN_FEATURES = [
  { key: 'NO_PLAN_FEATURE_CHAT', defaultValue: 'Chat with Buddy and your agents' },
  { key: 'NO_PLAN_FEATURE_KNOWLEDGE', defaultValue: 'Knowledge Base for your documents' },
  { key: 'NO_PLAN_FEATURE_SKILLS', defaultValue: 'Skills and saved Prompts' },
];

/**
 * Shown in place of a page that needs a plan. Offers the first sellable plan for the signed-in
 * person's own seat, and buys it through the same seat dialog as the plans page.
 */
export const NoPlanCard = ({ className }: { className?: string }) => {
  const { t } = useTranslation();
  const user = useCommonStore((s) => s.user);
  const isOnlyOrgUser = useCommonStore((s) => s.isOnlyOrgUser);
  const isDark = useTheme().resolvedTheme === 'dark';
  const { planSeats, isLoading } = usePlanSeats();
  const { givePlan, isPending, dialog } = useGivePlan(planSeats);
  const sellable = planSeats.filter((candidate) => candidate.plan && !isEnterprisePlanCode(candidate.code));
  // A plan already waiting for its first payment is finished or dropped, not bought again.
  const entry = sellable.find((candidate) => candidate.awaitingPayment) ?? sellable[0];
  const plan = entry?.plan;
  const intervals = uniquePlanIntervals(plan?.prices);
  const [chosenTab, setChosenTab] = useState<string>();
  const credits = useCreditSummary().data;
  const canTopUp = !isOnlyOrgUser && !!credits?.canTopUp;
  const isCreditsUsedUp =
    !!credits?.enabled &&
    credits.hasAccess !== false &&
    !!credits.lastTopUpAtUtc &&
    credits.balanceCredits <= 0;
  // With no plan to sell, top-up is the only thing this card can offer.
  const isTopUp = canTopUp && (chosenTab === TOP_UP_TAB || !plan);
  const chosenInterval = chosenTab === TOP_UP_TAB ? undefined : chosenTab;
  // A plan someone already bought keeps its billing cycle, so there is no cycle to choose.
  const isBought = !!entry?.pool;
  let interval = chosenInterval ?? (intervals.includes('month') ? 'month' : intervals[0]);
  if (entry && isBought) interval = defaultSeatInterval(entry);
  const price = plan ? preferredPrice(plan, interval) : undefined;
  const userName = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || user?.email || '';

  // A bought plan still gets its own cycle as a tab, so Top up always has a tab to sit next to.
  const tabs = [...(canTopUp ? [TOP_UP_TAB] : []), ...(isBought ? [interval] : intervals)];
  const tabToggle = tabs.length > 1 && (
    <BillingCycleToggle
      fullWidth
      intervals={tabs}
      selectedInterval={isTopUp ? TOP_UP_TAB : interval}
      disabled={isPending}
      onChange={setChosenTab}
    />
  );

  const handleBuy = () => {
    if (!entry || !user?.id) return;
    const showSwitched = () =>
      successToast({
        title: t('SUCCESS'),
        description: t('PLAN_SWITCHED_TO', { plan: entry.name, defaultValue: 'You are now on the {{plan}} plan.' }),
      });
    givePlan([{ userId: user.id, name: userName }], withInterval(entry, interval), undefined, showSwitched);
  };

  return (
    <div className={cn('flex w-full flex-col items-center px-4 py-10 font-sans', className)}>
      <Buddy inline size={120} />

      <span className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1 text-xs font-medium text-foreground">
        <Lock className="h-3 w-3" />
        {isCreditsUsedUp ? t('CREDITS_USED_UP_BADGE', 'Credits used up') : t('NO_ACTIVE_PLAN', 'No active plan')}
      </span>

      <div className="mt-4 flex items-center justify-center gap-3">
        <h1 className="text-center font-heyJune text-[28px] font-normal leading-tight text-foreground sm:text-[36px]">
          {isCreditsUsedUp
            ? t('CREDITS_USED_UP_TITLE', 'Your top-up credits are used up')
            : isOnlyOrgUser
              ? t('ASK_ADMIN_FOR_PLAN', 'Ask your admin for a plan')
              : t('CHOOSE_A_PLAN_TO_GET_STARTED', 'Choose a plan to get started')}
        </h1>
      </div>
      <p className="mt-3 max-w-md text-center text-sm text-muted-foreground">
        {isCreditsUsedUp
          ? isOnlyOrgUser
            ? t(
                'CREDITS_USED_UP_MEMBER_DESCRIPTION',
                'Ask your admin to activate a plan or top up again to keep using chat, agents and your knowledge base.'
              )
            : t(
                'CREDITS_USED_UP_DESCRIPTION',
                'Activate a plan or top up again to keep using chat, agents and your knowledge base.'
              )
          : isOnlyOrgUser
            ? t(
                'NO_PLAN_MEMBER_DESCRIPTION',
                'Only your admin can buy plans. Chat, agents and your knowledge base unlock as soon as they give you a seat.'
              )
            : t('NO_PLAN_DESCRIPTION', 'Chat, agents and your knowledge base unlock as soon as you have an active plan.')}
      </p>

      {/* Members without the admin role cannot buy plans, so they get no plan card. */}
      {!isOnlyOrgUser && (
        <div className="mt-8 w-full max-w-[560px] rounded-3xl border border-[#e8e4d8] bg-white p-5 dark:border-border dark:bg-card">
          {isLoading && (
            <div className="flex justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {!isLoading && isTopUp && credits && (
            <>
              {tabToggle}
              <div className="mt-5">
                <TopUpForm summary={credits} />
              </div>
            </>
          )}

          {!isLoading && !isTopUp && !plan && (
            <p className="py-6 text-center text-sm text-muted-foreground">
              {t(
                'NO_PLANS_DESC',
                'There are currently no subscription plans available for this organization. Please contact your administrator.'
              )}
            </p>
          )}

          {!isLoading && !isTopUp && plan && (
            <>
              {tabToggle}

              <div className="mt-5 flex items-start justify-between gap-4 border-b border-border pb-4">
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-foreground">{plan.displayName}</h2>
                  <p className="truncate text-xs text-muted-foreground">
                    {t('NO_PLAN_SEAT_FOR', { name: userName, defaultValue: '1 seat · for {{name}}' })}
                  </p>
                </div>
                {price && (
                  <div className="shrink-0 text-right">
                    <p className="text-xl font-bold text-foreground">
                      {formatMinor(
                        discountedAmountMinor(price.unitAmountMinor, price.automaticDiscountBasisPoints),
                        price.currencyCode
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {interval === 'year' ? t('PER_YEAR', 'per year') : t('PER_MONTH', 'per month')}
                    </p>
                  </div>
                )}
              </div>

              <ul className="mt-4 flex flex-col gap-2.5">
                {NO_PLAN_FEATURES.map((feature) => (
                  <li key={feature.key} className="flex items-center gap-2.5 text-sm text-foreground">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#FAF02A]">
                      <Check className="h-3 w-3 text-black" strokeWidth={3} />
                    </span>
                    {t(feature.key, feature.defaultValue)}
                  </li>
                ))}
              </ul>

              {entry?.awaitingPayment ? (
                <>
                  <div className="mt-5">
                    <PendingCheckoutAction entry={entry} />
                  </div>
                  <p className="mt-3 text-center text-xs text-muted-foreground">{planUnavailableNote(t, entry)}</p>
                </>
              ) : (
                <>
                  <Button
                    className={cn('mt-5 w-full rounded-full py-6 text-base font-semibold', primaryButtonClass(isDark))}
                    onClick={handleBuy}
                    disabled={isPending || !price}
                  >
                    {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {t('BUY_PLAN', { plan: plan.displayName, defaultValue: 'Buy {{plan}} plan' })}
                  </Button>
                  <p className="mt-3 text-center text-xs text-muted-foreground">
                    {t('BILLING_DETAILS_NEXT_STEP', "You'll add billing details on the next step.")}
                  </p>
                </>
              )}
            </>
          )}
        </div>
      )}

      <AddSeatDialog {...dialog} />
    </div>
  );
};
